"use strict";
// Copyright (C) Microsoft Corporation. All rights reserved.
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateDistance = exports.sortElements = exports.SortingMethod = exports.sortFocusContainers = void 0;
const cache_1 = require("../cache");
/**
 * Sorts the given focus containers top to bottom
 *
 *  @param focusContainers - the list of containers we want to sort
 */
const sortFocusContainers = (focusContainers) => {
    focusContainers.sort((c1, c2) => {
        const c1Element = c1.getElement();
        const c2Element = c2.getElement();
        if (!c1Element || !c2Element) {
            return 0;
        }
        // First sort by the top of the container. The containers with tops closer to the top of the screen should be first.
        // If they are near-equal, the shorter container should be first.
        const c1Rect = (0, cache_1.getCachedBoundingClientRect)(c1Element);
        const c2Rect = (0, cache_1.getCachedBoundingClientRect)(c2Element);
        if (!c1Rect || !c2Rect) {
            return 0;
        }
        const topDiff = Math.max(c1Rect.top, 0) - Math.max(c2Rect.top, 0);
        if (Math.abs(topDiff) < 1) {
            return c1Rect.height - c2Rect.height;
        }
        else {
            return topDiff;
        }
    });
};
exports.sortFocusContainers = sortFocusContainers;
/**
 * Enum representing different sorting methods.
 */
var SortingMethod;
(function (SortingMethod) {
    /**
     * Sorts by the top position, from lowest to highest.
     */
    SortingMethod["Top"] = "top";
    /**
     * Sorts by the top position, from highest to lowest.
     */
    SortingMethod["InvertedTop"] = "invertedTop";
    /**
     * Sorts by the left position, from lowest to highest.
     */
    SortingMethod["Left"] = "left";
    /**
     * Sorts by the left position, from highest to lowest.
     */
    SortingMethod["InvertedLeft"] = "invertedLeft";
    /**
     * Sorts by the bottom position, from lowest to highest.
     */
    SortingMethod["Bottom"] = "bottom";
    /**
     * Sorts by the bottom position, from highest to lowest.
     */
    SortingMethod["InvertedBottom"] = "invertedBottom";
    /**
     * Sorts by the right position, from lowest to highest.
     */
    SortingMethod["Right"] = "right";
    /**
     * Sorts by the right position, from highest to lowest.
     */
    SortingMethod["InvertedRight"] = "invertedRight";
})(SortingMethod || (exports.SortingMethod = SortingMethod = {}));
const SIMILARITY_THRESHOLD = 10;
/**
 * Sorts an array of HTML elements based on various sorting methods.
 *
 * @param elements - The array of HTMLElements to be sorted.
 * @param sortingMethods - An array of SortingMethod enums determining the sorting criteria.
 */
const sortElements = (elements, sortingMethods) => {
    elements.sort((a, b) => {
        const aRect = (0, cache_1.getCachedBoundingClientRect)(a);
        const bRect = (0, cache_1.getCachedBoundingClientRect)(b);
        for (let i = 0; i < sortingMethods.length; i++) {
            const lastSortingMethod = i === sortingMethods.length - 1;
            switch (sortingMethods[i]) {
                case SortingMethod.Top:
                    if (lastSortingMethod || Math.abs(aRect.top - bRect.top) > SIMILARITY_THRESHOLD) {
                        return aRect.top - bRect.top;
                    }
                    break;
                case SortingMethod.InvertedTop:
                    if (lastSortingMethod || Math.abs(aRect.top - bRect.top) > SIMILARITY_THRESHOLD) {
                        return bRect.top - aRect.top;
                    }
                    break;
                case SortingMethod.Left:
                    if (lastSortingMethod || Math.abs(aRect.left - bRect.left) > SIMILARITY_THRESHOLD) {
                        return aRect.left - bRect.left;
                    }
                    break;
                case SortingMethod.InvertedLeft:
                    if (lastSortingMethod || Math.abs(aRect.left - bRect.left) > SIMILARITY_THRESHOLD) {
                        return bRect.left - aRect.left;
                    }
                    break;
                case SortingMethod.Bottom:
                    if (lastSortingMethod || Math.abs(aRect.bottom - bRect.bottom) > SIMILARITY_THRESHOLD) {
                        return aRect.bottom - bRect.bottom;
                    }
                    break;
                case SortingMethod.InvertedBottom:
                    if (lastSortingMethod || Math.abs(aRect.bottom - bRect.bottom) > SIMILARITY_THRESHOLD) {
                        return bRect.bottom - aRect.bottom;
                    }
                    break;
                case SortingMethod.Right:
                    if (lastSortingMethod || Math.abs(aRect.right - bRect.right) > SIMILARITY_THRESHOLD) {
                        return aRect.right - bRect.right;
                    }
                    break;
                case SortingMethod.InvertedRight:
                    if (lastSortingMethod || Math.abs(aRect.right - bRect.right) > SIMILARITY_THRESHOLD) {
                        return bRect.right - aRect.right;
                    }
                    break;
            }
        }
        return 0;
    });
};
exports.sortElements = sortElements;
// Calculate the "distance" between elements based on their positions and dimensions
// A perfect match will result in a distance of 0
const calculateDistance = (el1, rect2) => {
    const rect1 = (0, cache_1.getCachedBoundingClientRect)(el1);
    // Calculate the horizontal and vertical distances
    const horizontalDistance = Math.max(rect1.left - rect2.right, rect2.left - rect1.right, 0);
    const verticalDistance = Math.max(rect1.top - rect2.bottom, rect2.top - rect1.bottom, 0);
    // Use Pythagorean theorem to calculate the Euclidean distance, with adjustments
    // to prioritize perfect matches
    return Math.sqrt(horizontalDistance * horizontalDistance + verticalDistance * verticalDistance);
};
exports.calculateDistance = calculateDistance;
//# sourceMappingURL=data:application/json;base64,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